import { defineStore } from 'pinia';
import type { RouteLocationNormalized, RouteMeta } from 'vue-router';

import {
  EnumOrganizationMemberPermissions,
  EnumOrganizationPermissions,
  EnumPermissionContextIds,
  EnumPermissionContexts,
  EnumUserPermissions,
} from '@/helpers/enums/permission.enum';
import type {
  AllOrgPermission,
  Categories,
  CategoriesPermission,
  Permission,
  PermissionObject,
  SinglePermissionSet,
} from '@/helpers/interfaces/admin/permission.interface';
import { axiosInstance, pinia } from '@/plugins';
import { authEnumRoutes } from '@/routes/auth/auth.enum.routes';
import { sharedEnumRoutes } from '@/routes/shared/shared.routes.enum';
import { ApiHandler as adminApi } from '@/submodules/adminApi/apiHandler';
import { ApiHandler } from '@/submodules/instructorApi/apiHandler';

import { useConfigurationStore } from '../admin/configuration.pinia';
import { useAuthStore } from '../auth/auth.pinia';

import { useToasterStore } from './toaster.pinia';

export const usePermissionStore = defineStore('permission', {
  state: () => ({
    systemPermissions: [] as Permission[],
    userPermissions: [] as Permission[],
    organzationPermissions: {} as AllOrgPermission,
    categoriesPermission: {} as CategoriesPermission,
    singlePermissionSet: {} as SinglePermissionSet,
    permissionsObject: [] as Categories[],
    permissionRecord: {} as Record<string, boolean>,
    systemPermissionsObject: undefined as PermissionObject | undefined,
    userPermissionsObject: undefined as PermissionObject | undefined,
  }),

  actions: {
    async getAllOrgPermissions(
      params: typeof adminApi.permissionSets.getAllOrgPermissionSets.params
    ) {
      const { path, method } = adminApi.permissionSets.getAllOrgPermissionSets;
      try {
        const { data, status } = await axiosInstance(path(), {
          method,
          params: {
            ...params,
            'contextSlugs[]': EnumPermissionContexts.OrganizationMember,
          },
        });
        this.organzationPermissions = data;
        return status;
      } catch (error) {
        return error;
      }
    },

    async getPermissionCategories(): Promise<number> {
      try {
        const { path, method } =
          adminApi.permissionSets.permissionsSystemCopy.permissionCategories
            .getAllPermissionCategories;
        const { data, status } = await axiosInstance(path(), {
          method,
        });
        this.categoriesPermission = data;
        return status;
      } catch (e) {
        return 500;
      }
    },
    async getSinglePermissionSet(orgPermissionSetId: number): Promise<number> {
      try {
        const { path, method } =
          adminApi.permissionSets.getSingleOrgPermissionSet;
        const { data, status } = await axiosInstance(
          path({
            orgPermissionSetId: orgPermissionSetId,
          }),
          {
            method,
          }
        );
        this.singlePermissionSet = data;
        return status;
      } catch (e) {
        return 500;
      }
    },
    async updateOrgPermissionSet(
      permission: {
        entityId: number;
        addOrRemove: string;
      }[],
      orgPermissionSetId: number
    ) {
      try {
        const { path, method } = adminApi.permissionSets.updateOrgPermissionSet;

        const { status } = await axiosInstance(
          path({
            orgPermissionSetId: orgPermissionSetId,
          }),
          {
            method,
            data: {
              permissions: permission,
            },
          }
        );
        return status;
      } catch (e) {
        return 500;
      }
    },
    // Control-base super-admin update — skips canBeEdited and context
    // checks so the super admin can toggle org-level feature permissions.
    async updatePermissionSet(
      permission: {
        entityId: number;
        addOrRemove: string;
      }[],
      permissionSetId: number
    ) {
      try {
        const { path, method } =
          adminApi.permissionSets.permissionsSystemCopy.permissionSets
            .updatePermissionSetByAdmin;
        const { status } = await axiosInstance(path({ permissionSetId }), {
          method,
          data: {
            permissions: permission,
          },
        });
        return status;
      } catch (e) {
        return 500;
      }
    },
    async changeOrgPermissionSetName(
      name: string,
      orgPermissionSetId: number
    ): Promise<number> {
      try {
        const { path, method } = adminApi.permissionSets.updateOrgPermissionSet;
        const { status } = await axiosInstance(
          path({ orgPermissionSetId: orgPermissionSetId }),
          {
            method,
            data: {
              name: name,
            },
          }
        );
        await this.getAllOrgPermissions({ skip: 0, take: 100 });
        return status;
      } catch (e) {
        return 500;
      }
    },
    async createOrgPermissionSet(name: string, permissionContextSlug: string) {
      try {
        const { path, method } = adminApi.permissionSets.createOrgPermissionSet;
        const { data } = await axiosInstance(path(), {
          method,
          data: {
            name: name,
            permissionContextSlug: permissionContextSlug,
          },
        });
        //@todo if any organization have more than 40 role we will do skip and take //by suhaib
        await this.getAllOrgPermissions({
          skip: 0,
          take: Number.MAX_SAFE_INTEGER,
        });
        useToasterStore().created();

        return data;
      } catch (e) {
        useToasterStore().error();
        return 500;
      }
    },
    async deleteOrgPermissionSet(orgPermissionSetId: number): Promise<number> {
      try {
        const { path, method } = adminApi.permissionSets.deleteOrgPermissionSet;
        const { status } = await axiosInstance(
          path({ orgPermissionSetId: orgPermissionSetId }),
          {
            method,
          }
        );
        //@todo if any organization have more than 40 role we will do skip and take //by suhaib
        await this.getAllOrgPermissions({
          skip: 0,
          take: Number.MAX_SAFE_INTEGER,
        });
        return status;
      } catch (e) {
        return 500;
      }
    },
    setPermissionObject() {
      // Only include OrganizationMember-context permissions in the member
      // permissions UI. Organization-context permissions have their own
      // page in the control-base.
      const ORGANIZATION_CONTEXT_ID = EnumPermissionContextIds.Organization;
      const permissionByCategory = this.systemPermissions.reduce(
        (
          acc: {
            [key: string]: Permission[];
          },
          permission: Permission
        ) => {
          if (
            permission.categoryId &&
            permission.contextId !== ORGANIZATION_CONTEXT_ID
          ) {
            if (acc[permission.categoryId]) {
              acc[permission.categoryId].push(permission);
            } else {
              acc[permission.categoryId] = [permission];
            }
          }
          return acc;
        },
        {}
      );
      this.permissionsObject = this.categoriesPermission?.data
        ?.map((parentCategory: Categories) => {
          const children = parentCategory.children
            .map((category) => ({
              ...category,
              permissions: permissionByCategory[category.id],
            }))
            .filter((c) => c.permissions?.length);
          return { ...parentCategory, children };
        })
        .filter((p) => p.children.length > 0 || p.slug === 'all');
    },

    async initializePermissions() {
      if (
        localStorage.getItem('access_token') &&
        localStorage.getItem('refresh_token')
      ) {
        await Promise.all([
          this.getSystemPermissions(),
          this.getUserPermissions(),
        ]);
        this.systemPermissionsObject = this.convertArrayToObject(
          this.systemPermissions
        );
        this.userPermissionsObject = this.convertArrayToObject(
          this.userPermissions
        );
      }
    },
    async getSystemPermissions() {
      try {
        const { path, method } =
          ApiHandler.misc.permissionsSystem.permissions.getAllPermissions;
        const { data } = await axiosInstance(path(), {
          method,
          params: {
            take: Number.MAX_SAFE_INTEGER,
          },
        });

        this.systemPermissions = data.data;
      } catch (error) {
        return error;
      }
    },
    async getUserPermissions() {
      try {
        const { path, method } = ApiHandler.auth.getUserPermissions;
        const { data } = await axiosInstance(path(), {
          method,
          params: {
            take: Number.MAX_SAFE_INTEGER,
          },
        });
        this.userPermissions = data.data;
      } catch (error) {
        return error;
      }
    },
    checkConfigKeys(meta: RouteMeta | undefined) {
      const configStore = useConfigurationStore();
      if (meta) {
        if (
          meta.shouldHaveCertificateConfig &&
          !configStore.certificatesEnabled
        )
          return false;
        if (
          meta.shouldHaveInternalTeamMode &&
          !configStore.internalTeamMode
        )
          return false;
        if (
          meta.hiddenInInternalTeamMode &&
          configStore.internalTeamMode
        )
          return false;
        if (
          meta.shouldHaveRegistrationApproval &&
          configStore.registrationRequiresApproval?.strategy !== 'manual'
        )
          return false;

        if (
          meta?.shouldHaveClickupIntegration &&
          !configStore.clickupLists?.length
        )
          return false;
        if (
          meta.shouldHaveTranslationEnabled &&
          !configStore.googleTranslationEnabled
        )
          return false;
        return true;
      }
    },
    async checkAuthentication(
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      beforeRoute: boolean = false
    ) {
      const meta = to.meta;
      const requiresAuth = (meta?.requiresAuth as boolean) ?? false;
      const requiresLogout = (meta?.requiresLogout as boolean) ?? false;
      const memberPermissions = (meta?.memberPermissions ??
        []) as Array<EnumOrganizationMemberPermissions>;
      const organizationPermissions = (meta?.organizationPermissions ??
        []) as Array<EnumOrganizationPermissions>;
      const memberPermissionsAny = (meta?.memberPermissionsAny ??
        []) as Array<EnumOrganizationMemberPermissions>;
      const organizationPermissionsAny = (meta?.organizationPermissionsAny ??
        []) as Array<EnumOrganizationPermissions>;
      const shouldHaveRegistrationApproval =
        meta.shouldHaveRegistrationApproval;
      const shouldHaveInternalTeamMode = meta.shouldHaveInternalTeamMode;
      const hiddenInInternalTeamMode = meta.hiddenInInternalTeamMode;

      if (!localStorage.getItem('access_token') && requiresAuth) {
        return beforeRoute ? { name: authEnumRoutes.login.name } : false;
      } else if (requiresLogout && useAuthStore().isLogin) {
        if (from.name) {
          return { name: from.name };
        } else {
          return await useAuthStore().redirectAfterLogin();
        }
      } else if (localStorage.getItem('access_token')) {
        const { isAdministrator, isCoach, isInstructor, isSuperAdmin } =
          useAuthStore().portals || {};
        if (
          (meta.isInstructor && isInstructor == false) ||
          (meta.isCoach && isCoach == false) ||
          (meta.isAdministrator && isAdministrator == false) ||
          (meta.isSuperAdmin && isSuperAdmin == false)
        ) {
          useToasterStore().addNotificationWithKey({
            text: 'auth.noAccessToThisPortalError',
            type: 'error',
            location: 'bottom center',
          });
          return await useAuthStore().redirectAfterLogin();
        }

        // Permission check: both member and org permissions must pass
        // when declared on the route. AND arrays require all; Any arrays
        // require at least one. Config flags checked separately.
        const memberOk =
          this.checkPermission(memberPermissions) &&
          this.checkPermissionAny(memberPermissionsAny);
        const orgOk =
          this.checkPermission(organizationPermissions) &&
          this.checkPermissionAny(organizationPermissionsAny);

        if (
          shouldHaveRegistrationApproval &&
          useConfigurationStore(pinia).registrationRequiresApproval
            ?.strategy !== 'manual'
        ) {
          return await useAuthStore().redirectAfterLogin();
        }

        if (
          shouldHaveInternalTeamMode &&
          !useConfigurationStore(pinia).internalTeamMode
        ) {
          return await useAuthStore().redirectAfterLogin();
        }

        if (
          hiddenInInternalTeamMode &&
          useConfigurationStore(pinia).internalTeamMode
        ) {
          return await useAuthStore().redirectAfterLogin();
        }

        if (!memberOk || !orgOk) {
          if (from?.name) {
            return { name: from.name };
          } else {
            useToasterStore().addNotificationWithKey({
              text: 'auth.noAccessToThisPortalError',
              type: 'error',
              location: 'bottom center',
            });
            return { name: sharedEnumRoutes.notAuth.name };
          }
        }

        if (to.name == sharedEnumRoutes.root.name) {
          return await useAuthStore().redirectAfterLogin();
        }

        return null;
      }
    },
    // Context → bypass slug mapping. Mirrors nest's
    // getBypassPermissionSlugBasedOnContext — every context must map to
    // its "everything" slug so that holders of that wildcard pass
    // same-context checks on either side of the wire.
    getBypassPermissionName(
      permissionContextId: EnumPermissionContextIds | undefined
    ) {
      switch (permissionContextId) {
        case EnumPermissionContextIds.OrganizationMember:
          return 'organization_member_full_access';
        case EnumPermissionContextIds.User:
          return 'user_everything';
        case EnumPermissionContextIds.Global:
          return 'global_everything';
        case EnumPermissionContextIds.Organization:
          return 'organization_everything';
        case EnumPermissionContextIds.CourseInstructor:
          return 'course_instructor_everything';
        default:
          return 'never_gonna_give_you_up';
      }
    },

    convertArrayToObject(permissionsArray: Permission[]) {
      return permissionsArray.reduce((acc, permission) => {
        acc[permission.slug] = permission;
        return acc;
      }, {} as PermissionObject);
    },

    checkPermission(
      permissions: Array<
        | EnumOrganizationMemberPermissions
        | EnumOrganizationPermissions
        | EnumUserPermissions
      >
    ) {
      if (permissions.length === 0) return true;
      const checkAllPermission = (
        permissions: Array<
          | EnumOrganizationMemberPermissions
          | EnumOrganizationPermissions
          | EnumUserPermissions
        >
      ): boolean => {
        return permissions.every(
          (
            permissionSlug:
              | EnumOrganizationMemberPermissions
              | EnumOrganizationPermissions
              | EnumUserPermissions
          ) => {
            if (!this.userPermissionsObject || !this.systemPermissionsObject)
              return false;

            const baypassPermission = this.getBypassPermissionName(
              this.systemPermissionsObject[permissionSlug]?.contextId
            );

            if (this.userPermissionsObject[baypassPermission]) {
              // Bypass grants the member-level ownership, but the target
              // permission's own prerequisites (typically org-level feature
              // flags) must still hold. This ensures that "full access"
              // admins only see features the org has enabled.
              const systemPermission =
                this.systemPermissionsObject[permissionSlug];
              if (
                systemPermission?.prerequisites &&
                systemPermission.prerequisites.length > 0
              ) {
                const targetPrereqsMet = systemPermission.prerequisites.every(
                  (pr) => checkAllPermission([pr.prerequisite.slug])
                );
                if (targetPrereqsMet) return true;
                // Target prereqs not met — fall through (will also fail
                // the normal check below, but keeps the logic consistent).
              } else {
                // No prerequisites on the target — bypass grants access.
                return true;
              }
            }

            if (permissionSlug in this.permissionRecord) {
              return this.permissionRecord[permissionSlug];
            }

            const hasPermissionInUser =
              permissionSlug in this.userPermissionsObject; // can be changed to  userPermissionsObject[permissionSlug];
            const hasPermissionInSystem =
              permissionSlug in this.systemPermissionsObject; // can be changed to  userPermissionsObject[permissionSlug];

            const hasPermission = hasPermissionInUser && hasPermissionInSystem;

            // Read prerequisites from systemPermissionsObject (which has
            // the full prerequisite chain), not userPermissionsObject
            // (which doesn't include prerequisites in the API response).
            const systemPermission =
              this.systemPermissionsObject[permissionSlug];

            if (
              hasPermission &&
              systemPermission?.prerequisites &&
              systemPermission.prerequisites.length > 0
            ) {
              const allPrerequisitesGranted =
                systemPermission.prerequisites.every((prerequisite) =>
                  checkAllPermission([prerequisite.prerequisite.slug])
                );

              if (!allPrerequisitesGranted) {
                this.permissionRecord[permissionSlug] = false;
                return false;
              }
            } else {
              this.permissionRecord[permissionSlug] = hasPermission;
            }
            return hasPermission;
          }
        );
      };

      return checkAllPermission(permissions);
    },

    checkPermissionAny(
      permissions: Array<
        | EnumOrganizationMemberPermissions
        | EnumOrganizationPermissions
        | EnumUserPermissions
      >
    ) {
      if (permissions.length === 0) return true;
      return permissions.some((slug) => this.checkPermission([slug]));
    },
  },
});
